import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Star, Trash2, Check, Image as ImageIcon, Upload, Plus, MessageSquare, Loader2, Euro, Save, Package, Images, FolderPlus, X } from "lucide-react";
import { supabase, PHOTOS_BUCKET, storagePathFromPublicUrl } from "@/lib/supabase";
import { usePricing, savePricing } from "@/contexts/PricingContext";
import { CATEGORY_LIST } from "@/contexts/VehicleContext";
import { TEXTILE_SERVICES } from "@/data/textileServices";
import { MECHANIC_SERVICES } from "@/data/mechanicPricing";
import { useContent, saveContent, uploadImage, deleteImages } from "@/contexts/ContentContext";

export default function AdminPage() {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (!ready) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#E50914] animate-spin" />
      </div>
    );
  }

  return session ? <Dashboard onLogout={() => supabase.auth.signOut()} /> : <Login />;
}

/* ---------------- LOGIN ---------------- */
function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) toast.error(error.message || "Connexion impossible");
    else toast.success("Connecté");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm p-8 bg-[#0A0A0A] border border-white/10 rounded-2xl space-y-5" data-testid="admin-login">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 rounded-lg bg-[#E50914] flex items-center justify-center"><Lock className="w-4 h-4" /></div>
          <div>
            <div className="font-black text-lg leading-none">Espace admin</div>
            <div className="text-xs text-white/40 mt-1">Nova Clean Service</div>
          </div>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email"
            className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 focus:outline-none focus:border-[#E50914]" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Mot de passe</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password"
            className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 focus:outline-none focus:border-[#E50914]" />
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] disabled:opacity-60 text-white font-bold uppercase text-sm tracking-wider px-6 py-3.5 rounded transition-all">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
        </button>
        <a href="/" className="block text-center text-xs text-white/40 hover:text-white/70">← Retour au site</a>
      </form>
    </div>
  );
}

/* ---------------- DASHBOARD ---------------- */
function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("avis");
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 sticky top-0 bg-[#050505]/90 backdrop-blur z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#E50914] flex items-center justify-center font-black">N</div>
            <span className="font-black">Admin · Nova Clean</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" className="text-xs uppercase tracking-wider text-white/50 hover:text-white px-3 py-2">Voir le site</a>
            <button onClick={onLogout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wider bg-white/5 border border-white/15 hover:border-[#E50914] px-3 py-2 rounded transition-colors">
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          <TabBtn active={tab === "avis"} onClick={() => setTab("avis")} icon={MessageSquare}>Avis</TabBtn>
          <TabBtn active={tab === "galerie"} onClick={() => setTab("galerie")} icon={ImageIcon}>Photos avant/après</TabBtn>
          <TabBtn active={tab === "tarifs"} onClick={() => setTab("tarifs")} icon={Euro}>Tarifs</TabBtn>
          <TabBtn active={tab === "articles"} onClick={() => setTab("articles")} icon={Package}>Articles</TabBtn>
          <TabBtn active={tab === "images"} onClick={() => setTab("images")} icon={Images}>Images</TabBtn>
          <TabBtn active={tab === "classeurs"} onClick={() => setTab("classeurs")} icon={FolderPlus}>Classeurs</TabBtn>
        </div>
        {tab === "avis" && <ReviewsAdmin />}
        {tab === "galerie" && <GalleryAdmin />}
        {tab === "tarifs" && <PricingAdmin />}
        {tab === "articles" && <ArticlesAdmin />}
        {tab === "images" && <ImagesAdmin />}
        {tab === "classeurs" && <AlbumsAdmin />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children }) {
  return (
    <button onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider border transition-all ${
        active ? "bg-[#E50914] border-[#E50914]" : "bg-transparent border-white/15 text-white/60 hover:text-white"
      }`}>
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}

/* ---------------- AVIS ---------------- */
function ReviewsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ author: "", role: "", comment: "", source: "google", rating: 5 });

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from("reviews").select("*").order("created_at", { ascending: false });
    if (error) toast.error("Chargement impossible");
    else setItems(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    const { error } = await supabase.from("reviews").update({ status: "approved" }).eq("id", id);
    if (error) return toast.error("Erreur");
    toast.success("Avis publié");
    load();
  };
  const remove = async (id) => {
    const { error } = await supabase.from("reviews").delete().eq("id", id);
    if (error) return toast.error("Erreur");
    toast.success("Avis supprimé");
    load();
  };
  const add = async (e) => {
    e.preventDefault();
    if (!form.author || !form.comment) return toast.error("Nom et avis requis");
    const { error } = await supabase.from("reviews").insert({
      author: form.author, role: form.role || null, comment: form.comment,
      rating: form.rating, source: form.source, status: "approved",
    });
    if (error) return toast.error("Erreur");
    toast.success("Avis ajouté et publié");
    setForm({ author: "", role: "", comment: "", source: "google", rating: 5 });
    load();
  };

  const pending = items.filter((i) => i.status === "pending");
  const approved = items.filter((i) => i.status === "approved");

  return (
    <div className="space-y-10">
      {/* Ajout manuel (ex: recopier un avis Google) */}
      <section className="p-6 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl">
        <h3 className="font-black mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-[#E50914]" /> Ajouter un avis manuellement</h3>
        <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input placeholder="Nom" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })}
            className="bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]" />
          <input placeholder="Prestation (optionnel)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]" />
          <textarea placeholder="Avis" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
            className="sm:col-span-2 bg-[#050505] border border-white/15 rounded px-3 py-2.5 resize-none focus:outline-none focus:border-[#E50914]" rows="2" />
          <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
            className="bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]">
            <option value="google">Source : Google</option>
            <option value="site">Source : Site</option>
          </select>
          <select value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value) })}
            className="bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]">
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoile{n > 1 ? "s" : ""}</option>)}
          </select>
          <button className="sm:col-span-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] font-bold uppercase text-sm tracking-wider py-3 rounded transition-all">Ajouter</button>
        </form>
      </section>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#E50914]" /> : (
        <>
          <div>
            <h3 className="font-black mb-4">À valider {pending.length > 0 && <span className="text-[#E50914]">({pending.length})</span>}</h3>
            {pending.length === 0 ? <p className="text-white/40 text-sm">Aucun avis en attente.</p> : (
              <div className="space-y-3">{pending.map((r) => (
                <AdminReviewRow key={r.id} r={r} onApprove={() => approve(r.id)} onDelete={() => remove(r.id)} />
              ))}</div>
            )}
          </div>
          <div>
            <h3 className="font-black mb-4">Publiés ({approved.length})</h3>
            <div className="space-y-3">{approved.map((r) => (
              <AdminReviewRow key={r.id} r={r} onDelete={() => remove(r.id)} />
            ))}</div>
          </div>
        </>
      )}
    </div>
  );
}

function AdminReviewRow({ r, onApprove, onDelete }) {
  return (
    <div className="flex items-start gap-4 p-4 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl" data-testid={`admin-review-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-bold text-sm">{r.author}</span>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/15 text-white/60">
            {r.source === "google" ? "Google" : "Site"}
          </span>
          <span className="flex items-center gap-0.5">
            {[...Array(r.rating || 5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-[#E50914] text-[#E50914]" />)}
          </span>
        </div>
        {r.role && <div className="text-xs text-white/50 mb-1">{r.role}</div>}
        <p className="text-sm text-white/80">{r.comment}</p>
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        {onApprove && (
          <button onClick={onApprove} aria-label="Publier"
            className="inline-flex items-center gap-1 text-xs font-bold uppercase bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/30 px-3 py-2 rounded transition-colors">
            <Check className="w-4 h-4" /> Publier
          </button>
        )}
        <button onClick={onDelete} aria-label="Supprimer"
          className="inline-flex items-center gap-1 text-xs font-bold uppercase bg-white/5 border border-white/15 text-white/60 hover:border-[#E50914] hover:text-[#E50914] px-3 py-2 rounded transition-colors">
          <Trash2 className="w-4 h-4" /> Supprimer
        </button>
      </div>
    </div>
  );
}

/* ---------------- GALERIE ---------------- */
function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.from("gallery_items").select("*").order("created_at", { ascending: false });
    if (error) toast.error("Chargement impossible");
    else setItems(data || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const uploadOne = async (file, prefix) => {
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const path = `${prefix}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await supabase.storage.from(PHOTOS_BUCKET).upload(path, file, { upsert: false });
    if (error) throw error;
    return supabase.storage.from(PHOTOS_BUCKET).getPublicUrl(path).data.publicUrl;
  };

  const add = async (e) => {
    e.preventDefault();
    if (!beforeFile || !afterFile) return toast.error("Choisissez une photo Avant ET une photo Après");
    setUploading(true);
    try {
      const [before_url, after_url] = await Promise.all([uploadOne(beforeFile, "before"), uploadOne(afterFile, "after")]);
      const { error } = await supabase.from("gallery_items").insert({ title: title || null, before_url, after_url });
      if (error) throw error;
      toast.success("Photo avant/après ajoutée");
      setTitle(""); setBeforeFile(null); setAfterFile(null);
      e.target.reset();
      load();
    } catch (err) {
      toast.error("Échec de l'upload : " + (err.message || ""));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (item) => {
    const { error } = await supabase.from("gallery_items").delete().eq("id", item.id);
    if (error) return toast.error("Erreur");
    // suppression best-effort des fichiers du stockage
    const paths = [item.before_url, item.after_url].map(storagePathFromPublicUrl).filter(Boolean);
    if (paths.length) supabase.storage.from(PHOTOS_BUCKET).remove(paths);
    toast.success("Supprimé");
    load();
  };

  return (
    <div className="space-y-10">
      <section className="p-6 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl">
        <h3 className="font-black mb-4 flex items-center gap-2"><Upload className="w-4 h-4 text-[#E50914]" /> Ajouter une photo avant/après</h3>
        <form onSubmit={add} className="space-y-4">
          <input placeholder="Titre (ex : Pack complet — Clio)" value={title} onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FileField label="Photo AVANT" onChange={setBeforeFile} file={beforeFile} testid="admin-before" />
            <FileField label="Photo APRÈS" onChange={setAfterFile} file={afterFile} testid="admin-after" />
          </div>
          <button disabled={uploading} data-testid="admin-gallery-add"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] disabled:opacity-60 font-bold uppercase text-sm tracking-wider py-3 rounded transition-all">
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Plus className="w-4 h-4" /> Ajouter</>}
          </button>
        </form>
      </section>

      <div>
        <h3 className="font-black mb-4">Photos en ligne ({items.length})</h3>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#E50914]" /> : items.length === 0 ? (
          <p className="text-white/40 text-sm">Aucune photo. Ajoutez-en une ci-dessus.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.map((it) => (
              <div key={it.id} className="bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl overflow-hidden" data-testid={`admin-gallery-${it.id}`}>
                <div className="grid grid-cols-2">
                  <img src={it.before_url} alt="Avant" className="w-full h-32 object-cover" />
                  <img src={it.after_url} alt="Après" className="w-full h-32 object-cover" />
                </div>
                <div className="flex items-center justify-between p-3">
                  <span className="text-sm font-semibold truncate">{it.title || "Sans titre"}</span>
                  <button onClick={() => remove(it)} aria-label="Supprimer"
                    className="inline-flex items-center gap-1 text-xs font-bold uppercase text-white/60 hover:text-[#E50914] transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FileField({ label, onChange, file, testid }) {
  return (
    <label className="block cursor-pointer">
      <span className="block text-xs uppercase tracking-wider text-white/60 mb-2">{label}</span>
      <div className="flex items-center gap-2 bg-[#050505] border border-white/15 rounded px-3 py-2.5 hover:border-[#E50914] transition-colors">
        <ImageIcon className="w-4 h-4 text-white/40 shrink-0" />
        <span className="text-sm text-white/60 truncate">{file ? file.name : "Choisir un fichier…"}</span>
      </div>
      <input type="file" accept="image/*" data-testid={testid} className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)} />
    </label>
  );
}

/* ---------------- TARIFS ---------------- */
const AUTO_FORMULAS = [
  { key: "exterieur", label: "Extérieur" },
  { key: "interieur", label: "Intérieur" },
  { key: "pack", label: "Pack complet" },
  { key: "concession", label: "Concession" },
];
const AUTO_SUPP_LABELS = {
  shampooing_5_sieges: "Shampooing 5 sièges",
  shampooing_siege: "Shampooing siège (unité)",
  shampooing_sols: "Shampooing des sols",
  poils: "Poils d'animaux (dès)",
  tres_sale: "Véhicule très sale (dès)",
};
const SURFACE_LABELS = {
  box_1p: "Box — 1 place", box_2p: "Box — 2 places", parking: "Parking (par place)",
  balcon: "Balcon (au m²)", terrasse: "Terrasse (au m²)", cour: "Cour / Allée (au m²)",
};

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function PriceField({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-white/60 mb-1.5">{label}</span>
      <div className="relative">
        <input type="number" min="0" inputMode="decimal" value={value} data-testid={testid}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#050505] border border-white/15 rounded px-3 py-2.5 text-white focus:outline-none focus:border-[#E50914]" />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">€</span>
      </div>
    </label>
  );
}

function PricingAdmin() {
  const { pricing, reload } = usePricing();
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(pricing)));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(JSON.parse(JSON.stringify(pricing))); }, [pricing]);

  const setAuto = (cat, key, val) =>
    setDraft((d) => ({ ...d, auto: { ...d.auto, [cat]: { ...d.auto[cat], [key]: num(val) } } }));
  const setSupp = (key, val) =>
    setDraft((d) => ({ ...d, auto: { ...d.auto, supplements: { ...d.auto.supplements, [key]: num(val) } } }));
  const setTextile = (id, val) =>
    setDraft((d) => ({ ...d, textile: { ...d.textile, [id]: num(val) } }));
  const setSurface = (id, val) =>
    setDraft((d) => ({ ...d, surfaces: { ...d.surfaces, [id]: num(val) } }));

  const save = async () => {
    setSaving(true);
    const err = await savePricing(draft);
    setSaving(false);
    if (err) return toast.error("Échec de l'enregistrement : " + (err.message || ""));
    toast.success("Tarifs enregistrés ✓");
    reload();
  };

  return (
    <div className="space-y-10 pb-24">
      {/* Auto */}
      <section className="p-6 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl">
        <h3 className="font-black mb-1">Nettoyage auto</h3>
        <p className="text-xs text-white/40 mb-5">Prix par catégorie de véhicule (A à D) et par formule.</p>
        <div className="space-y-5">
          {CATEGORY_LIST.map((c) => (
            <div key={c.id}>
              <div className="text-sm font-bold text-white mb-2">Catégorie {c.id} <span className="text-white/40 font-normal">— {c.name}</span></div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {AUTO_FORMULAS.map((f) => (
                  <PriceField key={f.key} label={f.label} value={draft.auto[c.id]?.[f.key] ?? ""}
                    onChange={(v) => setAuto(c.id, f.key, v)} testid={`price-auto-${c.id}-${f.key}`} />
                ))}
              </div>
            </div>
          ))}
          <div>
            <div className="text-sm font-bold text-white mb-2">Options supplémentaires</div>
            <div className="grid grid-cols-2 gap-3">
              {Object.keys(AUTO_SUPP_LABELS).map((k) => (
                <PriceField key={k} label={AUTO_SUPP_LABELS[k]} value={draft.auto.supplements?.[k] ?? ""}
                  onChange={(v) => setSupp(k, v)} testid={`price-supp-${k}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Textile */}
      <section className="p-6 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl">
        <h3 className="font-black mb-1">Textile maison</h3>
        <p className="text-xs text-white/40 mb-5">Prix par article.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {TEXTILE_SERVICES.map((srv) => (
            <PriceField key={srv.id} label={srv.name} value={draft.textile?.[srv.id] ?? ""}
              onChange={(v) => setTextile(srv.id, v)} testid={`price-textile-${srv.id}`} />
          ))}
        </div>
      </section>

      {/* Surfaces */}
      <section className="p-6 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl">
        <h3 className="font-black mb-1">Surfaces extérieures</h3>
        <p className="text-xs text-white/40 mb-5">Box (prix fixe), parking (par place), petites surfaces (au m²).</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {Object.keys(SURFACE_LABELS).map((id) => (
            <PriceField key={id} label={SURFACE_LABELS[id]} value={draft.surfaces?.[id] ?? ""}
              onChange={(v) => setSurface(id, v)} testid={`price-surface-${id}`} />
          ))}
        </div>
      </section>

      {/* Save bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#050505]/95 backdrop-blur border-t border-white/10 p-4 z-20">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <span className="text-xs text-white/50">Les modifications s'appliquent au site après enregistrement (rafraîchir la page publique).</span>
          <button onClick={save} disabled={saving} data-testid="price-save"
            className="inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] disabled:opacity-60 text-white font-bold uppercase text-sm tracking-wider px-6 py-3 rounded transition-all">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer les tarifs
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- ARTICLES (ajout/suppression) ---------------- */
const ART_CATS = [
  { id: "textile", label: "Mobilier textile", hasImage: true, hasKind: false, nameLabel: "Nom de l'article", pricePH: "Prix (€)" },
  { id: "mecanique", label: "Mécanique", hasImage: true, hasKind: false, nameLabel: "Nom du service", pricePH: "Prix (€) — vide = sur devis" },
  { id: "surfaces", label: "Surfaces", hasImage: true, hasKind: true, nameLabel: "Nom de la surface", pricePH: "Prix (€/m² ou —)" },
  { id: "auto_supplements", label: "Options auto", hasImage: false, hasKind: false, nameLabel: "Nom de l'option", pricePH: "Prix (€)" },
];

function ArticlesAdmin() {
  const { content, reload } = useContent();
  const [cat, setCat] = useState("textile");
  const [form, setForm] = useState({ name: "", desc: "", price: "", kind: "m2" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const opt = ART_CATS.find((c) => c.id === cat);
  const list = content.custom?.[cat] || [];

  const add = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Le nom est requis");
    setBusy(true);
    try {
      let image = null;
      if (opt.hasImage && file) image = await uploadImage(file, "articles");
      const id = `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const item = cat === "auto_supplements"
        ? { id, label: form.name, desc: form.desc, price: Number(form.price) || 0 }
        : { id, name: form.name, desc: form.desc, price: form.price === "" ? 0 : Number(form.price), image, ...(opt.hasKind ? { kind: form.kind } : {}) };
      const err = await saveContent({ ...content, custom: { ...content.custom, [cat]: [...list, item] } });
      if (err) throw err;
      toast.success("Article ajouté");
      setForm({ name: "", desc: "", price: "", kind: "m2" });
      setFile(null);
      if (e.target.reset) e.target.reset();
      reload();
    } catch (err) {
      toast.error("Erreur : " + (err.message || ""));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    const err = await saveContent({ ...content, custom: { ...content.custom, [cat]: list.filter((x) => x.id !== item.id) } });
    if (err) return toast.error("Erreur");
    if (item.image) deleteImages([item.image]);
    toast.success("Supprimé");
    reload();
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-2">
        {ART_CATS.map((c) => (
          <button key={c.id} onClick={() => setCat(c.id)}
            className={`px-3 py-2 rounded-lg text-sm font-bold border transition-all ${cat === c.id ? "bg-[#E50914] border-[#E50914]" : "bg-transparent border-white/15 text-white/60 hover:text-white"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <section className="p-6 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl">
        <h3 className="font-black mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-[#E50914]" /> Ajouter dans « {opt.label} »</h3>
        <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input placeholder={opt.nameLabel} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]" />
          <input placeholder="Prix" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
            className="bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]" />
          <input placeholder="Description courte" value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })}
            className="sm:col-span-2 bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]" />
          {opt.hasKind && (
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]">
              <option value="m2">Tarif au m²</option>
              <option value="devis">Sur devis</option>
            </select>
          )}
          {opt.hasImage && <FileField label="Photo (optionnel)" onChange={setFile} file={file} testid="article-photo" />}
          <button disabled={busy} className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] disabled:opacity-60 font-bold uppercase text-sm tracking-wider py-3 rounded transition-all">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Plus className="w-4 h-4" /> Ajouter l'article</>}
          </button>
        </form>
        <p className="text-xs text-white/40 mt-3">Astuce : pour la Mécanique, laisse le prix vide pour afficher « Sur devis ».</p>
      </section>

      <div>
        <h3 className="font-black mb-4">Articles ajoutés ({list.length})</h3>
        {list.length === 0 ? <p className="text-white/40 text-sm">Aucun article personnalisé dans cette catégorie.</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {list.map((it) => (
              <div key={it.id} className="flex items-center gap-3 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl p-3">
                {it.image ? <img src={it.image} alt="" className="w-14 h-14 rounded object-cover shrink-0" /> : <div className="w-14 h-14 rounded bg-white/5 border border-white/10 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm truncate">{it.name || it.label}</div>
                  <div className="text-xs text-white/50 truncate">{it.desc}</div>
                  <div className="text-xs text-[#E50914] font-bold">{Number(it.price) > 0 ? `${it.price}€${it.kind === "m2" ? "/m²" : ""}` : "Sur devis"}</div>
                </div>
                <button onClick={() => remove(it)} aria-label="Supprimer" className="text-white/60 hover:text-[#E50914] transition-colors shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- IMAGES des articles existants ---------------- */
function ImagesAdmin() {
  const { content, reload } = useContent();
  const builtins = [
    { group: "Mobilier textile", items: TEXTILE_SERVICES.map((s) => ({ id: s.id, name: s.name, def: s.image })) },
    { group: "Mécanique", items: MECHANIC_SERVICES.map((s) => ({ id: s.id, name: s.name, def: null })) },
  ];

  const setImg = async (id, file) => {
    try {
      const url = await uploadImage(file, "articles");
      const err = await saveContent({ ...content, images: { ...content.images, [id]: url } });
      if (err) throw err;
      toast.success("Image mise à jour");
      reload();
    } catch (e) {
      toast.error("Erreur : " + (e.message || ""));
    }
  };

  const resetImg = async (id) => {
    const imgs = { ...content.images };
    const old = imgs[id];
    delete imgs[id];
    const err = await saveContent({ ...content, images: imgs });
    if (err) return toast.error("Erreur");
    if (old) deleteImages([old]);
    toast.success("Image réinitialisée");
    reload();
  };

  return (
    <div className="space-y-8">
      <p className="text-sm text-white/50">Change la photo des articles existants. Laisse vide pour garder la photo par défaut.</p>
      {builtins.map((g) => (
        <section key={g.group} className="p-6 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl">
          <h3 className="font-black mb-4">{g.group}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {g.items.map((it) => (
              <ImageRow key={it.id} item={it} current={content.images?.[it.id]} onUpload={(f) => setImg(it.id, f)} onReset={() => resetImg(it.id)} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ImageRow({ item, current, onUpload, onReset }) {
  const [busy, setBusy] = useState(false);
  const handle = async (f) => { if (!f) return; setBusy(true); await onUpload(f); setBusy(false); };
  const src = current || item.def;
  return (
    <div className="flex items-center gap-3 bg-[#050505] border border-white/10 rounded-xl p-3">
      {src ? <img src={src} alt="" className="w-14 h-14 rounded object-cover shrink-0" /> : <div className="w-14 h-14 rounded bg-white/5 border border-white/10 shrink-0" />}
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm truncate">{item.name}</div>
        <div className="text-xs text-white/40">{current ? "Photo personnalisée" : "Photo par défaut"}</div>
      </div>
      <label className="cursor-pointer inline-flex items-center gap-1 text-xs font-bold uppercase bg-white/5 border border-white/15 hover:border-[#E50914] px-2.5 py-2 rounded transition-colors shrink-0">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files?.[0])} />
      </label>
      {current && (
        <button onClick={onReset} aria-label="Réinitialiser" className="text-white/50 hover:text-[#E50914] transition-colors shrink-0"><X className="w-4 h-4" /></button>
      )}
    </div>
  );
}

/* ---------------- CLASSEURS (albums avant/après) ---------------- */
function AlbumsAdmin() {
  const { content, reload } = useContent();
  const albums = content.albums || [];
  const [vehicle, setVehicle] = useState("");
  const [service, setService] = useState("");
  const [cover, setCover] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!vehicle && !service) return toast.error("Indiquez le véhicule ou la prestation");
    if (!cover) return toast.error("Choisissez une image de couverture");
    setBusy(true);
    try {
      const coverUrl = await uploadImage(cover, "albums");
      const photoUrls = await Promise.all([...photos].map((f) => uploadImage(f, "albums")));
      const id = `a_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const album = { id, cover: coverUrl, vehicle, service, photos: photoUrls };
      const err = await saveContent({ ...content, albums: [album, ...albums] });
      if (err) throw err;
      toast.success("Classeur créé");
      setVehicle(""); setService(""); setCover(null); setPhotos([]);
      if (e.target.reset) e.target.reset();
      reload();
    } catch (err) {
      toast.error("Erreur : " + (err.message || ""));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (album) => {
    const err = await saveContent({ ...content, albums: albums.filter((a) => a.id !== album.id) });
    if (err) return toast.error("Erreur");
    deleteImages([album.cover, ...(album.photos || [])]);
    toast.success("Supprimé");
    reload();
  };

  return (
    <div className="space-y-8">
      <section className="p-6 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl">
        <h3 className="font-black mb-4 flex items-center gap-2"><FolderPlus className="w-4 h-4 text-[#E50914]" /> Créer un classeur</h3>
        <form onSubmit={add} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input placeholder="Véhicule (ex : Clio 4)" value={vehicle} onChange={(e) => setVehicle(e.target.value)}
              className="bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]" />
            <input placeholder="Prestation (ex : Pack complet)" value={service} onChange={(e) => setService(e.target.value)}
              className="bg-[#050505] border border-white/15 rounded px-3 py-2.5 focus:outline-none focus:border-[#E50914]" />
          </div>
          <FileField label="Image de couverture" onChange={setCover} file={cover} testid="album-cover" />
          <label className="block cursor-pointer">
            <span className="block text-xs uppercase tracking-wider text-white/60 mb-2">Photos du classeur (plusieurs possibles)</span>
            <div className="flex items-center gap-2 bg-[#050505] border border-white/15 rounded px-3 py-2.5 hover:border-[#E50914] transition-colors">
              <ImageIcon className="w-4 h-4 text-white/40 shrink-0" />
              <span className="text-sm text-white/60 truncate">{photos.length ? `${photos.length} photo(s) sélectionnée(s)` : "Choisir des photos…"}</span>
            </div>
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setPhotos(Array.from(e.target.files || []))} />
          </label>
          <button disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] disabled:opacity-60 font-bold uppercase text-sm tracking-wider py-3 rounded transition-all">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Plus className="w-4 h-4" /> Créer le classeur</>}
          </button>
        </form>
      </section>

      <div>
        <h3 className="font-black mb-4">Classeurs en ligne ({albums.length})</h3>
        {albums.length === 0 ? <p className="text-white/40 text-sm">Aucun classeur. Créez-en un ci-dessus.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {albums.map((a) => (
              <div key={a.id} className="bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl overflow-hidden">
                <div className="aspect-[4/3] bg-white/5">{a.cover && <img src={a.cover} alt="" className="w-full h-full object-cover" />}</div>
                <div className="p-3">
                  <div className="font-bold text-sm truncate">{a.vehicle || "Véhicule"}</div>
                  <div className="text-xs text-white/50 truncate">{a.service} · {(a.photos || []).length} photo(s)</div>
                  <button onClick={() => remove(a)} className="mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase text-white/60 hover:text-[#E50914] transition-colors">
                    <Trash2 className="w-4 h-4" /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
